import { requisitosDeEnvio } from './whatsapp-template.js';

/**
 * U14 F5: regras puras do Broadcast (disparo de template do WhatsApp para um público), usadas pelo servidor (Automações e
 * Conversas) e pela tela. Ficha `demandas/entrada/2026-10-15_malgo_MOD_automacoes-canvas-instagram-whatsapp.md`, seções
 * "Pesquisa > WhatsApp", "Arquitetura" item 10 e "Consentimento para o Broadcast (opt-in)".
 */

// ---------------------------------------------------------------------------
// Limites da Meta e do MalgoOS
// ---------------------------------------------------------------------------

export const LIMITES_BROADCAST = {
  /** Vazão da Meta por número: 80 mensagens por segundo. O ritmo do disparo fica sempre abaixo. */
  ritmoMaximo: 80,
  ritmoMinimo: 1,
  ritmoPadrao: 20,
  /** Destinatários por disparo (decisão reversível: acima disso, dividir em mais de um disparo). */
  destinatarios: 10_000,
  /** Linhas da planilha importada. */
  linhasPlanilha: 10_000,
  /** Colunas extras da planilha (fora telefone e nome) que podem virar variável. */
  colunasPlanilha: 20,
  valorColuna: 200,
  nome: 120,
  /** Valor de uma variável do template depois de preenchida (limite da Meta para parâmetro de texto). */
  valorVariavel: 1024,
  /** Tentativas por destinatário nos erros passageiros (130429, 131056, rede). */
  tentativas: 3,
  /** O código do botão vale este tempo depois do envio (o clique tardio ainda conta). */
  validadeBotaoDias: 30,
  /** Agendamento até este tempo à frente. */
  agendamentoDias: 90,
} as const;

/**
 * Limite da Meta de destinatários únicos por portfólio em 24 h (sobe sozinho com a qualidade do número). `null` = ilimitado.
 * O disparo para quando a conta das últimas 24 h chega nele e retoma quando a janela libera.
 */
export const LIMITES_24H = [250, 2_000, 10_000, 100_000, null] as const;
export type Limite24h = (typeof LIMITES_24H)[number];

export const ESTADOS_BROADCAST = ['rascunho', 'agendado', 'enviando', 'pausado', 'concluido', 'cancelado'] as const;
export type EstadoBroadcast = (typeof ESTADOS_BROADCAST)[number];

export const ESTADOS_DESTINATARIO = ['pendente', 'enviando', 'enviado', 'entregue', 'lido', 'falhou', 'ignorado'] as const;
export type EstadoDestinatario = (typeof ESTADOS_DESTINATARIO)[number];

/** Por que o destinatário não recebeu (fica no relatório, sem envio). */
export const MOTIVOS_IGNORADO = ['sem_consentimento', 'saiu', 'telefone_invalido', 'repetido', 'variavel_vazia', 'cancelado'] as const;
export type MotivoIgnorado = (typeof MOTIVOS_IGNORADO)[number];

// ---------------------------------------------------------------------------
// Público
// ---------------------------------------------------------------------------

export const FONTES_PUBLICO = ['etiquetas', 'cadastros', 'segmento', 'planilha'] as const;
export type FontePublico = (typeof FONTES_PUBLICO)[number];

/** Filtro do público. Etiquetas: as da conversa do WhatsApp; segmento: tags e setores do cadastro. */
export interface Publico {
  fonte: FontePublico;
  etiquetas: string[];
  parceiros: string[];
  tags: string[];
  setores: string[];
}

export const publicoVazio = (): Publico => ({ fonte: 'etiquetas', etiquetas: [], parceiros: [], tags: [], setores: [] });

/** Linha da planilha já lida (telefone ainda bruto: o servidor normaliza e recusa o inválido). */
export interface LinhaPlanilha {
  telefone: string;
  nome: string;
  colunas: Record<string, string>;
}

const APELIDOS_TELEFONE = ['telefone', 'celular', 'whatsapp', 'fone', 'phone', 'numero', 'número'];
const APELIDOS_NOME = ['nome', 'name', 'contato', 'cliente'];

const normalizarCabecalho = (t: string) =>
  t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** Divide uma linha CSV (aspas duplas, separador dado). */
function dividirLinha(linha: string, sep: string): string[] {
  const campos: string[] = [];
  let atual = '';
  let aspas = false;
  for (let i = 0; i < linha.length; i++) {
    const ch = linha[i]!;
    if (aspas) {
      if (ch === '"' && linha[i + 1] === '"') {
        atual += '"';
        i++;
      } else if (ch === '"') aspas = false;
      else atual += ch;
    } else if (ch === '"') aspas = true;
    else if (ch === sep) {
      campos.push(atual);
      atual = '';
    } else atual += ch;
  }
  campos.push(atual);
  return campos.map((c) => c.trim());
}

export type ErroPlanilha = 'planilha.vazia' | 'planilha.sem_telefone' | 'planilha.linhas_demais' | 'planilha.colunas_demais';

/**
 * Lê a planilha em CSV (separador `;` ou `,`, primeira linha com os nomes das colunas). A coluna do telefone é a que se chama
 * telefone, celular, whatsapp...; a do nome, nome ou contato. As outras viram colunas que a variável do template pode usar.
 */
export function lerPlanilha(texto: string): { ok: true; linhas: LinhaPlanilha[]; colunas: string[] } | { ok: false; erro: ErroPlanilha } {
  const linhas = texto.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim());
  if (linhas.length < 2) return { ok: false, erro: 'planilha.vazia' };
  const sep = (linhas[0]!.match(/;/g)?.length ?? 0) >= (linhas[0]!.match(/,/g)?.length ?? 0) ? ';' : ',';
  const cab = dividirLinha(linhas[0]!, sep).map(normalizarCabecalho);
  const iTel = cab.findIndex((c) => APELIDOS_TELEFONE.map(normalizarCabecalho).includes(c));
  if (iTel < 0) return { ok: false, erro: 'planilha.sem_telefone' };
  const iNome = cab.findIndex((c) => APELIDOS_NOME.includes(c));
  const extras = cab.map((c, i) => ({ c, i })).filter(({ c, i }) => i !== iTel && i !== iNome && c);
  if (extras.length > LIMITES_BROADCAST.colunasPlanilha) return { ok: false, erro: 'planilha.colunas_demais' };
  if (linhas.length - 1 > LIMITES_BROADCAST.linhasPlanilha) return { ok: false, erro: 'planilha.linhas_demais' };
  const saida: LinhaPlanilha[] = [];
  for (const bruta of linhas.slice(1)) {
    const v = dividirLinha(bruta, sep);
    const colunas: Record<string, string> = {};
    for (const { c, i } of extras) colunas[c] = (v[i] ?? '').slice(0, LIMITES_BROADCAST.valorColuna);
    saida.push({ telefone: v[iTel] ?? '', nome: (iNome >= 0 ? (v[iNome] ?? '') : '').slice(0, LIMITES_BROADCAST.nome), colunas });
  }
  return { ok: true, linhas: saida, colunas: extras.map((e) => e.c) };
}

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

/** Categoria do template (`conversas_templates.categoria`) → tipo de consentimento que ela exige. Autenticação não dispara. */
export type TipoConsentimento = 'avisos' | 'marketing';
export function tipoDoTemplate(categoria: string): TipoConsentimento | null {
  if (categoria === 'marketing') return 'marketing';
  if (categoria === 'utilidade') return 'avisos';
  return null;
}

export type TipoBotaoDisparo = 'resposta' | 'link' | 'link_dinamico' | 'telefone' | 'codigo' | 'outro';
export interface BotaoDoDisparo {
  indice: number;
  tipo: TipoBotaoDisparo;
  rotulo: string;
}
export interface ResumoTemplate {
  corpo: string;
  /** Quantas variáveis o corpo pede (posicionais `{{1}}` ou nomeadas `{{nome}}`, a regra da fase 4). */
  variaveis: number;
  /** Os nomes delas, na ordem de envio (`1`, `2`... ou `nome`, `guia`...): o rótulo de cada uma na tela. */
  nomes: string[];
  cabecalho: { tipo: 'texto'; texto: string; variaveis: number; nomes: string[] } | { tipo: 'imagem' | 'video' | 'documento' } | null;
  rodape: string | null;
  botoes: BotaoDoDisparo[];
}

interface ComponenteMeta {
  type?: unknown;
  format?: unknown;
  text?: unknown;
  url?: unknown;
  buttons?: unknown;
}

/**
 * Lê os componentes do template no formato da Meta (corpo, cabeçalho, rodapé e botões pela posição). As variáveis e os
 * parâmetros dos botões seguem `requisitosDeEnvio` (U14 F4), a mesma leitura do envio no Conversas.
 */
export function resumoTemplate(componentes: unknown): ResumoTemplate {
  const lista = (Array.isArray(componentes) ? componentes : []) as ComponenteMeta[];
  const de = (tipo: string) => lista.find((c) => String(c?.type ?? '').toUpperCase() === tipo);
  const req = requisitosDeEnvio(componentes);
  const corpo = String(de('BODY')?.text ?? '');
  const h = de('HEADER');
  const cabecalho: ResumoTemplate['cabecalho'] =
    req.cabecalho && req.cabecalho.tipo !== 'texto'
      ? { tipo: req.cabecalho.tipo }
      : h && String(h.format ?? 'TEXT').toUpperCase() === 'TEXT'
        ? { tipo: 'texto', texto: String(h.text ?? ''), variaveis: req.cabecalho?.tipo === 'texto' ? req.cabecalho.variaveis.length : 0, nomes: req.cabecalho?.tipo === 'texto' ? req.cabecalho.variaveis : [] }
        : null;
  const comParametro = new Map(req.botoes.map((b) => [b.indice, b.tipo]));
  const bts = (Array.isArray(de('BUTTONS')?.buttons) ? (de('BUTTONS')!.buttons as ComponenteMeta[]) : []).slice(0, 10);
  const botoes = bts.map((b, indice): BotaoDoDisparo => {
    const t = String(b?.type ?? '').toUpperCase();
    const par = comParametro.get(indice);
    const tipo: TipoBotaoDisparo = req.respostas.includes(indice) ? 'resposta' : par === 'link' ? 'link_dinamico' : par === 'codigo' ? 'codigo' : t === 'URL' ? 'link' : t === 'PHONE_NUMBER' ? 'telefone' : 'outro';
    return { indice, tipo, rotulo: String(b?.text ?? '').slice(0, 25) };
  });
  return { corpo, variaveis: req.corpo.length, nomes: req.corpo, cabecalho, rodape: de('FOOTER') ? String(de('FOOTER')!.text ?? '') : null, botoes };
}

// ---------------------------------------------------------------------------
// Variáveis e botões do disparo
// ---------------------------------------------------------------------------

/** Campos do cadastro (ou da planilha) que uma variável pode usar. */
export const CAMPOS_VARIAVEL = ['nome', 'primeiro_nome', 'telefone', 'email', 'codigo'] as const;
export type CampoVariavel = (typeof CAMPOS_VARIAVEL)[number];

/** De onde vem o valor de uma variável: campo do cadastro, coluna da planilha ou texto fixo. */
export type OrigemVariavel = { origem: 'campo'; campo: CampoVariavel } | { origem: 'coluna'; coluna: string } | { origem: 'fixo'; valor: string };

/** Dados do destinatário para preencher as variáveis. */
export interface DadosDestinatario {
  nome: string;
  telefone: string;
  email: string | null;
  codigo: string | null;
  colunas: Record<string, string>;
}

export function valorDaVariavel(o: OrigemVariavel, d: DadosDestinatario): string {
  switch (o.origem) {
    case 'fixo':
      return o.valor.trim();
    case 'coluna':
      return (d.colunas[o.coluna] ?? '').trim();
    case 'campo':
      if (o.campo === 'primeiro_nome') return d.nome.trim().split(/\s+/)[0] ?? '';
      if (o.campo === 'nome') return d.nome.trim();
      if (o.campo === 'telefone') return d.telefone;
      if (o.campo === 'email') return (d.email ?? '').trim();
      return (d.codigo ?? '').trim();
  }
}

/** O que acontece no clique de cada botão de resposta rápida do template. */
export const ACOES_BOTAO = ['nada', 'parar', 'fluxo'] as const;
export type AcaoBotao = (typeof ACOES_BOTAO)[number];
export interface BotaoBroadcast {
  indice: number;
  acao: AcaoBotao;
  /** Com `acao: 'fluxo'`: a automação (do grupo, ativa, com o gatilho "Clique em botão de broadcast"). */
  fluxo_id: string | null;
}

/** Botão do template que tem cara de "Parar de receber" (a ação padrão dele no disparo). */
export function botaoPareceParar(rotulo: string): boolean {
  const t = normalizarFrase(rotulo);
  return /\b(parar|sair|stop|cancelar|descadastrar)\b/.test(t) || t.includes('nao quero');
}

/** Parâmetros do cabeçalho e dos botões que não são resposta rápida (link dinâmico e código). */
export interface ExtrasBroadcast {
  /** Cabeçalho de texto: uma origem por variável; de mídia: o link https. */
  cabecalho: { variaveis: OrigemVariavel[] } | { link: string } | null;
  /** Final da URL dinâmica (por botão) e o código do "copiar código". */
  botoes: ({ indice: number; tipo: 'link'; valor: OrigemVariavel } | { indice: number; tipo: 'codigo'; codigo: string })[];
}

export type ProblemaBroadcast =
  | 'broadcast.template_categoria'
  | 'broadcast.variaveis'
  | 'broadcast.cabecalho'
  | 'broadcast.botao_parar'
  | 'broadcast.botao_fluxo'
  | 'broadcast.botoes'
  | 'broadcast.publico_vazio'
  | 'broadcast.declaracao';

/**
 * Confere a configuração contra o template (a mesma regra na tela e no servidor). Marketing exige um botão "Parar de receber"
 * (resposta rápida com a ação `parar`): é o opt-out da ficha, que a Meta também pede nos templates de marketing.
 */
export function problemasDoBroadcast(d: { tipo: TipoConsentimento | null; template: ResumoTemplate; variaveis: OrigemVariavel[]; extras: ExtrasBroadcast; botoes: BotaoBroadcast[]; colunas: string[] }): ProblemaBroadcast[] {
  const p: ProblemaBroadcast[] = [];
  if (!d.tipo) p.push('broadcast.template_categoria');
  const vOk = (o: OrigemVariavel) => (o.origem === 'fixo' ? !!o.valor.trim() && o.valor.length <= LIMITES_BROADCAST.valorVariavel : o.origem === 'coluna' ? d.colunas.includes(o.coluna) : CAMPOS_VARIAVEL.includes(o.campo));
  if (d.variaveis.length !== d.template.variaveis || !d.variaveis.every(vOk)) p.push('broadcast.variaveis');
  const c = d.template.cabecalho;
  if (c?.tipo === 'texto' && c.variaveis > 0) {
    if (!d.extras.cabecalho || !('variaveis' in d.extras.cabecalho) || d.extras.cabecalho.variaveis.length !== c.variaveis || !d.extras.cabecalho.variaveis.every(vOk)) p.push('broadcast.cabecalho');
  } else if (c && c.tipo !== 'texto') {
    if (!d.extras.cabecalho || !('link' in d.extras.cabecalho) || !/^https:\/\/\S{4,500}$/.test(d.extras.cabecalho.link)) p.push('broadcast.cabecalho');
  }
  const respostas = d.template.botoes.filter((b) => b.tipo === 'resposta');
  const porIndice = new Map(d.botoes.map((b) => [b.indice, b]));
  if (d.botoes.some((b) => !respostas.some((r) => r.indice === b.indice)) || porIndice.size !== d.botoes.length) p.push('broadcast.botoes');
  for (const b of d.template.botoes) {
    if (b.tipo === 'link_dinamico' && !d.extras.botoes.some((x) => x.indice === b.indice && x.tipo === 'link' && vOk(x.valor))) p.push('broadcast.botoes');
    if (b.tipo === 'codigo' && !d.extras.botoes.some((x) => x.indice === b.indice && x.tipo === 'codigo' && /^\S{1,15}$/.test(x.codigo))) p.push('broadcast.botoes');
  }
  if (d.botoes.some((b) => b.acao === 'fluxo' && !b.fluxo_id)) p.push('broadcast.botao_fluxo');
  if (d.tipo === 'marketing' && !d.botoes.some((b) => b.acao === 'parar')) p.push('broadcast.botao_parar');
  return [...new Set(p)];
}

// ---------------------------------------------------------------------------
// Erros do WhatsApp (relatório e nova tentativa)
// ---------------------------------------------------------------------------

/**
 * Erros da API Cloud tratados no relatório (ficha, "Erros a tratar"). `definitivo`: não repete (o mesmo envio falharia de
 * novo); `pausa`: o problema é do disparo inteiro (template), que para para alguém conferir.
 */
export const ERROS_WHATSAPP: Record<number, { definitivo: boolean; pausa?: boolean }> = {
  131047: { definitivo: true }, // janela de 24 h fechada
  131026: { definitivo: true }, // número sem WhatsApp ou que não aceita a mensagem
  131048: { definitivo: true }, // restrição por spam (qualidade do número)
  131049: { definitivo: true }, // limite de marketing por usuário (a Meta pede esperar 24 h: não repete neste disparo)
  131056: { definitivo: false }, // muitas mensagens ao mesmo número em pouco tempo
  130429: { definitivo: false }, // vazão do número
  132000: { definitivo: true }, // número de variáveis diferente do template
  132001: { definitivo: true, pausa: true }, // template inexistente ou não aprovado
};
export const CODIGOS_ERRO_WHATSAPP = Object.keys(ERROS_WHATSAPP).map(Number);

/** Código da Meta no motivo gravado na mensagem (`131026: Message undeliverable`), ou nulo. */
export function codigoDoMotivo(motivo: string | null | undefined): number | null {
  const m = /^(\d{3,7})\b/.exec(String(motivo ?? '').trim());
  return m ? Number(m[1]) : null;
}

/** O envio com este código pode ser repetido mais tarde? Código desconhecido não repete (erro definitivo da ficha). */
export function erroPassageiro(codigo: number | null): boolean {
  return codigo !== null && ERROS_WHATSAPP[codigo]?.definitivo === false;
}

// ---------------------------------------------------------------------------
// Opt-out escrito na conversa
// ---------------------------------------------------------------------------

/** Frases que, sozinhas na mensagem, tiram o contato da lista de marketing (ficha: "parar", "sair", "não quero"). */
export const PALAVRAS_SAIDA = ['parar', 'sair', 'nao quero', 'parar de receber', 'stop'] as const;

function normalizarFrase(t: string): string {
  return t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A mensagem inteira é um pedido de saída? Frase mais longa que contenha "sair" ("vou sair às 5") não conta. */
export function ehPedidoDeSaida(texto: string): boolean {
  return (PALAVRAS_SAIDA as readonly string[]).includes(normalizarFrase(texto));
}

// ---------------------------------------------------------------------------
// Consentimento
// ---------------------------------------------------------------------------

export const ORIGENS_CONSENTIMENTO = ['contrato', 'conversa', 'formulario', 'importacao', 'manual', 'botao_parar', 'palavra'] as const;
export type OrigemConsentimento = (typeof ORIGENS_CONSENTIMENTO)[number];
export const SITUACOES_CONSENTIMENTO = ['autorizou', 'saiu'] as const;
export type SituacaoConsentimento = (typeof SITUACOES_CONSENTIMENTO)[number];
